import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import { ChevronRight, LogOut, Menu, Search } from "lucide-react";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAdminLogout } from "../../services/auth/logout";
import { useAdminSession } from "../../services/auth/me";

interface AdminTopBarProps {
  onCommandPaletteOpen: () => void;
  onMobileNavOpen: () => void;
}

const PRETTY_PATH_LABELS: Record<string, string> = {
  "": "Overview",
  campaigns: "Campaigns",
  new: "New",
  users: "Users",
  revenue: "Revenue",
  analytics: "Analytics",
  audit: "Audit log",
};

const formatCrumb = (segment: string) => {
  if (PRETTY_PATH_LABELS[segment] !== undefined) return PRETTY_PATH_LABELS[segment];
  // Anything else (cuids, slugs) gets the same monospaced treatment as
  // table IDs — keeps the breadcrumb honest about what's user-supplied
  // versus what's a route name.
  return segment.length > 12 ? `${segment.slice(0, 8)}…` : segment;
};

const isOpaqueId = (segment: string) => /^[A-Za-z0-9_-]{12,}$/.test(segment);

/**
 * Stripe-Dashboard-style top bar.
 *
 * Layout:
 *   [breadcrumb]                 [env badge (centered)]                 [⌘K · UserMenu]
 *
 * - 56px tall, bottom border only (NO shadow). Card background.
 * - The env badge reads "Test mode" in dev / "Live" in prod, mirroring
 *   the Stripe convention. Live is intentionally understated (no green
 *   dot) — the only time you should be reaching for affirmation is when
 *   you're in test mode.
 */
export const AdminTopBar = ({ onCommandPaletteOpen, onMobileNavOpen }: AdminTopBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAdminSession();
  const { logout } = useAdminLogout();

  const segments = React.useMemo(
    () => location.pathname.split("/").filter(Boolean),
    [location.pathname],
  );

  const isLive = import.meta.env.PROD;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-card/95 px-3 backdrop-blur sm:gap-4 sm:px-6">
      <button
        type="button"
        onClick={onMobileNavOpen}
        aria-label="Open navigation"
        className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface hover:text-foreground lg:hidden"
      >
        <Menu className="size-4" />
      </button>

      <nav
        aria-label="Breadcrumb"
        className="flex min-w-0 flex-1 items-center text-[13px] lg:flex-none"
      >
        <button
          type="button"
          onClick={() => navigate("/")}
          className="hidden text-muted-foreground transition-colors hover:text-foreground sm:inline"
        >
          Admin
        </button>
        {segments.length === 0 ? (
          <>
            {/* On mobile we drop the leading "Admin" crumb to save horizontal space, so
                the chevron in front of the current page would dangle. Hide it on mobile. */}
            <ChevronRight className="mx-1.5 hidden size-3.5 text-subtle sm:inline" />
            <span className="font-medium text-foreground">Overview</span>
          </>
        ) : (
          segments.map((seg, idx) => {
            const href = "/" + segments.slice(0, idx + 1).join("/");
            const isLast = idx === segments.length - 1;
            return (
              <React.Fragment key={href}>
                <ChevronRight
                  className={cn(
                    "mx-1.5 size-3.5 text-subtle",
                    // First chevron only matters when "Admin" is visible (sm+).
                    idx === 0 && "hidden sm:inline",
                  )}
                />
                {isLast ? (
                  <span
                    className={cn(
                      "truncate font-medium text-foreground",
                      isOpaqueId(seg) && "mono text-[12px] text-muted-foreground",
                    )}
                  >
                    {formatCrumb(seg)}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate(href)}
                    className="truncate text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {formatCrumb(seg)}
                  </button>
                )}
              </React.Fragment>
            );
          })
        )}
      </nav>

      <div className="hidden flex-1 justify-center sm:flex">
        <span
          className={cn(
            "rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em]",
            isLive
              ? "border-border bg-surface text-muted-foreground"
              : "border-warning/40 bg-warning-soft text-warning-foreground",
          )}
        >
          {isLive ? "Live" : "Test mode"}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onCommandPaletteOpen}
          className="h-8 gap-2 border-border bg-card text-[12px] text-muted-foreground hover:bg-surface hover:text-foreground"
        >
          <Search className="size-3.5" />
          <span className="hidden md:inline">Search…</span>
          <kbd className="ml-1 hidden items-center gap-0.5 text-[10px] text-subtle md:inline-flex">
            <span className="rounded border border-border bg-background px-1 font-mono">⌘</span>
            <span className="rounded border border-border bg-background px-1 font-mono">K</span>
          </kbd>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-8 items-center gap-2 rounded-md border border-border bg-card px-2 text-[12px] font-medium text-foreground transition-colors duration-120 ease-out hover:bg-surface"
            >
              <span className="flex size-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary">
                {(user?.name ?? user?.email ?? "?").slice(0, 1)}
              </span>
              <span className="hidden max-w-[140px] truncate sm:inline">{user?.email ?? "—"}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-[13px] font-semibold text-foreground">
                  {user?.name ?? "Admin"}
                </span>
                <span className="text-[11px] font-normal text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleLogout} className="text-danger-foreground">
              <LogOut className="mr-2 size-3.5" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
